import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import { ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams, } from "ag-grid-enterprise";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { FilterDef } from "../../../constants/FinanceModels";
import { formatDate } from "../../../utils/FormatUtils";
import { getLocalGridState, setLocalGridState, } from "../../../utils/GridUtils";
import { STATE_SUMMARY_UPDATING } from "../../../constants/FinanceRecoil";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import ColouredChip from "../../../component/ColouredChip";
import { STATE_ACTIVE_FILTERS, STATE_TABLE_UPDATING } from "../pages/atoms/CollectionAtoms";
import { useSddRepository } from "../../../api/repositories/SddRepository";
import { IconButton, Tooltip } from "@mui/material";
import { DescriptionRounded, DownloadRounded } from "@mui/icons-material";
import FinanceDynamicActions from "../../../component/FinanceDynamicActions";
import { useNavigate } from "react-router-dom";
import { convertTypology, MANDATE_TYPES, REPORT_TYPES, STATUSES } from "../constants/CollectionConstants";
import useToggle from "../hooks/useToggle";
import CollectionsModal from "../sections/CollectionsModal";
import { useAgGridLocale } from "../../../../../config/globalConfig";


const CollectionTable = () => {
    const UNIQUE_ID = "fnn-sdd-mandates";

    const { t } = useTranslation("finance", { keyPrefix: "sdd.collections" });

    const navigate = useNavigate();

    const { getCollections } = useSddRepository();

    const filterDefs = useRecoilValue(STATE_ACTIVE_FILTERS);
    const [updating, setUpdating] = useRecoilState(STATE_TABLE_UPDATING);
    const [currentRow, setCurrentRow] = useState<any>();

    const [showDownload, toggleDownload] = useToggle();

    const setSummaryUpdating = useSetRecoilState(STATE_SUMMARY_UPDATING);

    const gridRef = useRef<AgGridReact>(null);
    const agGridLocale = useAgGridLocale();

    const [columnDefs] = useState<ColDef[]>([
        {
            field: 'type', headerName: t('table.report-type'), headerTooltip: t('table.report-type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(REPORT_TYPES, params.value)),
        },
        {
            field: 'mandateType', headerName: t('table.mandate-type'), headerTooltip: t('table.mandate-type'),
            minWidth: 200,
            unSortIcon: true,
            valueFormatter: params => t(convertTypology(MANDATE_TYPES, params.value))
        },
        {
            field: "reportDate",
            headerName: t("table.report-date"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
        },
        {
            field: "receiptDate",
            headerName: t("table.receipt-date"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
            valueFormatter: (params: any) => formatDate(params.value),
            
        },
        {
            field: "receiptTotalAmount",
            headerName: t("table.receipt-total-amount"),
            minWidth: 200,
            unSortIcon: true,
            sortable: true,
        },

        {
            field: "status",
            headerName: t("table.status"),
            minWidth: 100,
            unSortIcon: true,
            sortable: true,
            pinned: "right",
            lockPosition: "right",
            lockVisible: true,
            lockPinned: true,
            cellRendererSelector: (params) => {
                let status: string = params.data.status;
                let color = STATUSES.find(s => s.value === status)?.color?.split(".")[0];
                let tooltipText: string = params.data.errorReason;
                if (!color || color === "neutral") color = "default";
                return {
                    component: () => (
                        <ColouredChip name={status} color={color} tooltip={tooltipText} />
                    ),
                };
            },
        },
        {
            field: '_actions', headerName: '',
            suppressSizeToFit: true, minWidth: 100, maxWidth: 110, sortable: false,
            pinned: 'right', lockPosition: 'right', lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                let visible = params.data.status === "ERROR" ? true : false;
                function downloadElement() {
                        setCurrentRow(params.data);      
                        toggleDownload();
                }
                return {
                    component: () =>

                        <FinanceDynamicActions>
                            <Tooltip arrow placement={"top"} title={t("table.actions.download")}>
                                <IconButton color="accent" disabled={visible} onClick={() => {
                                    
                                    downloadElement()
                                }}>
                                    <DownloadRounded />
                                </IconButton>
                            </Tooltip>
                            <Tooltip arrow placement={"top"} title={t("table.actions.details")}>
                                <IconButton color="accent" disabled={visible} onClick={() => {
                                    navigate(`/finance/payments/sdd/collections/${params.data.id}?reportType=${params.data.type}&reportDate=${params.data.reportDate}`)
                                }}>
                                    <DescriptionRounded />
                                </IconButton>
                            </Tooltip>
                        </FinanceDynamicActions>
                };
            },
            suppressFiltersToolPanel: true
        }
    ]);



    function isBeforeToday(date: string) {
        const today: Date = new Date();
        const comparisonDate: Date = new Date(date);
        return today > comparisonDate;
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data["id"],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
            headerCheckboxSelection: true
        },
        noRowsOverlayComponentParams: {
            title: t("table.no-rows.title"),
            content: t("table.no-rows.content")
        },
    };

    useEffect(() => {
        gridRef.current?.api?.setServerSideDatasource(
            createDatasource(filterDefs, gridRef.current.api)
        );
        setSummaryUpdating(true);
    }, [filterDefs]);

    useEffect(() => {
    }, [currentRow]);

    useEffect(() => {
        if (updating) {
            gridRef.current?.api?.setServerSideDatasource(
                createDatasource(filterDefs, gridRef.current.api)
            );
            setUpdating(false);
            setSummaryUpdating(true);
        }
    }, [updating]);

    useEffect(() => {
        setSummaryUpdating(true);
    }, [agGridLocale]);

    const createDatasource = (filterDefs: FilterDef[], api: GridApi<any>) => {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let startRow = params.request.startRow ? params.request.startRow : 0;
                let endRow = params.request.endRow ? params.request.endRow : 10;
                let sort =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].colId;
                let order =
                    params.request.sortModel.length === 0
                        ? null
                        : params.request.sortModel[0].sort.toUpperCase();
                getCollections(startRow, endRow - startRow, sort, order, filterDefs)
                    .then((response) => {
                        let result = response.result;

                        if (result["total-amount"] === 0) {
                            api.showNoRowsOverlay();
                        } else {
                            api.hideOverlay();
                        }

                        params.success({
                            rowData: result.content,
                            rowCount: result["total-amount"],
                        });
                    })
                    .catch(() => params.fail());
            },
        };
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        /*         renderInfo: () => (
                    //<NumericSummary query={() => getSummary()} fields={[ID_ACTIVE, ID_INACTIVE]} i18nPath={"collection.counters"}/>
                ), */
        /*         renderActions: () => (
                    <Button fullWidth color={"accent"} variant="contained" component="label"
                        startIcon={<DownloadRounded />}
                        disabled={isSelectedRow}
                        onClick={() => {
                            toggleDownload()
                        }}>
                        {t('buttons.download-report')} 
                    </Button>
                ), */
    };

    function saveState() {
        setLocalGridState(UNIQUE_ID, {
            rows: gridRef.current!.api.paginationGetPageSize(),
            cols: gridRef.current!.columnApi.getColumnState(),
        });
    }

    return (
        <>

            {showDownload &&
                <CollectionsModal
                    open={showDownload}
                    viewNote={true}
                    form={{currentRow}}
                    title={t("modal.download.collectiontitle")}
                    body={isBeforeToday(currentRow.receiptDate) ? t("modal.download.collectionBodyMessage-expired") : t("modal.download.collectionBodyMessage")}
                    leftButton={t("modal.download.actionButtons.cancel")}
                    leftFunction={toggleDownload}
                    rightButton={t("modal.download.actionButtons.confirm")}
                    rigthFunction={() => { }}
                    onClose={toggleDownload}
                />
            }
            <AgGridCustomTable
                toolbar={toolbar}
                rowModelType="serverSide"
                paginationPageSize={getLocalGridState(UNIQUE_ID).rows}
                serverSideInfiniteScroll
                gridRef={gridRef}
                columnDefs={columnDefs}
                gridOptions={gridOptions}
                rowSelection={"multiple"}
                suppressRowClickSelection
                noRowsOverlayComponent={noRowsOverlayComponent}
                onGridReady={() => {
                    gridRef.current!.api.setServerSideDatasource(
                        createDatasource(filterDefs, gridRef.current!.api)
                    );

                    let gridState = getLocalGridState(UNIQUE_ID).cols;
                    if (gridState !== undefined) {
                        gridRef.current!.columnApi.applyColumnState({
                            state: gridState,
                            applyOrder: true,
                        });
                    }
                }}
                onColumnMoved={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnPinned={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onColumnResized={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onSortChanged={(e) => {
                    if (e.source === "api") return;
                    saveState();
                }}
                onPaginationChanged={() => {
                    if (gridRef.current!.api) {
                        setLocalGridState(UNIQUE_ID, {
                            rows: gridRef.current!.api?.paginationGetPageSize(),
                            cols: getLocalGridState(UNIQUE_ID).cols,
                        });
                    }
                }}
            ></AgGridCustomTable>


        </>
    );
};

export default CollectionTable;
